/**
 * Copyright 2025 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT-0
 */

import { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, TextField, Switch,
  FormControlLabel, Button, Alert, Snackbar, InputAdornment, Tabs, Tab,
  CircularProgress, Divider, Table, TableBody, TableCell, TableHead,
  TableRow, Chip, Paper, TableContainer,
} from '@mui/material';
import { AttachMoney, Settings as SettingsIcon, Security, Save, Info, Map as MapIcon, AddLocation, Sms, SystemUpdate, Apple, Android, VerifiedUser, Add as AddIcon, DeleteOutline } from '@mui/icons-material';
import { generateClient } from 'aws-amplify/api';
import DrawServiceAreaDialog from '../components/DrawServiceAreaDialog';
import ViewServiceAreaDialog from '../components/ViewServiceAreaDialog';
import { extractGraphQLError } from '../utils/graphqlError';

const client = generateClient();

const GET_PRICING_CONFIG = `
  query GetPricingConfig {
    getPricingConfig {
      baseFare distanceRate timeRate minimumFare currency surgeMultiplier
      tierMultipliers { economy comfort business }
      serviceAreaEnforced maxTripDurationMinutes
      updatedAt
    }
  }
`;

const LIST_SERVICE_AREAS = `
  query ListServiceAreas {
    listServiceAreas {
      items { country city serviceActive displayName }
    }
  }
`;

const UPDATE_SERVICE_AREA = `
  mutation UpdateServiceArea($input: UpdateServiceAreaInput!) {
    updateServiceArea(input: $input) { country city serviceActive displayName }
  }
`;

const UPDATE_PRICING_CONFIG = `
  mutation UpdatePricingConfig($input: PricingConfigInput!) {
    updatePricingConfig(input: $input) {
      baseFare distanceRate timeRate minimumFare currency surgeMultiplier updatedAt
    }
  }
`;

const GET_SYSTEM_CONFIG = `
  query GetSystemConfig {
    getSystemConfig { otpProvider updatedAt }
  }
`;

const UPDATE_SYSTEM_CONFIG = `
  mutation UpdateSystemConfig($input: SystemConfigInput!) {
    updateSystemConfig(input: $input) { otpProvider updatedAt }
  }
`;

const APP_UPDATE_FIELDS = `
  driver {
    ios { latestVersion minSupportedVersion storeUrl }
    android { latestVersion minSupportedVersion storeUrl }
  }
  rider {
    ios { latestVersion minSupportedVersion storeUrl }
    android { latestVersion minSupportedVersion storeUrl }
  }
  updateMessage
  updatedAt
`;

const GET_APP_UPDATE_CONFIG = `
  query GetAppUpdateConfig {
    getAppUpdateConfig { ${APP_UPDATE_FIELDS} }
  }
`;

const UPDATE_APP_UPDATE_CONFIG = `
  mutation UpdateAppUpdateConfig($input: AppUpdateConfigInput!) {
    updateAppUpdateConfig(input: $input) { ${APP_UPDATE_FIELDS} }
  }
`;

const REVIEW_ACCOUNT_FIELDS = `accounts { phoneNumber code label } updatedAt`;

const GET_REVIEW_ACCOUNTS = `
  query GetReviewAccounts {
    getReviewAccounts { ${REVIEW_ACCOUNT_FIELDS} }
  }
`;

const UPDATE_REVIEW_ACCOUNTS = `
  mutation UpdateReviewAccounts($input: ReviewAccountsConfigInput!) {
    updateReviewAccounts(input: $input) { ${REVIEW_ACCOUNT_FIELDS} }
  }
`;

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
function TabPanel({ children, value, index }: TabPanelProps) {
  return <div hidden={value !== index}>{value === index && <Box sx={{ p: 3 }}>{children}</Box>}</div>;
}

const EXAMPLE_TRIPS = [
  { label: 'Short trip',  distanceKm: 3,  durationMin: 8  },
  { label: 'Medium trip', distanceKm: 10, durationMin: 20 },
  { label: 'Long trip',   distanceKm: 25, durationMin: 45 },
];

const TIERS = ['economy', 'comfort', 'business'] as const;
const TIER_LABELS: Record<string, string> = { economy: '🚗 Economy', comfort: '🚙 Comfort', business: '🏎️ Business' };
const PLATFORM_FEE = 0.25;

function calcFare(
  distanceKm: number, durationMin: number,
  pricing: { baseFare: number; distanceRate: number; timeRate: number; minimumFare: number; surgeMultiplier: number; tierMultipliers: Record<string, number> },
  tier: string
): number {
  const base = pricing.baseFare + distanceKm * pricing.distanceRate + durationMin * pricing.timeRate;
  const afterMin = Math.max(base, pricing.minimumFare);
  const afterSurge = afterMin * pricing.surgeMultiplier;
  return Math.round(afterSurge * (pricing.tierMultipliers[tier] ?? 1.0));
}

function PricingImpactPanel({ pricing }: { pricing: any }) {
  return (
    <Box mt={4}>
      <Divider sx={{ mb: 3 }} />
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Info color="primary" fontSize="small" />
        <Typography variant="h6">Live Pricing Impact</Typography>
      </Box>
      <Typography variant="body2" color="textSecondary" mb={2}>
        Updates in real-time as you adjust values above. Shows what a rider pays and what a driver earns for typical trips.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom>How fare is calculated</Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 13, color: 'text.secondary' }}>
          fare = baseFare + (distance × distanceRate) + (duration × timeRate)<br />
          fare = max(fare, minimumFare)<br />
          fare = fare × surgeMultiplier<br />
          fare = fare × tierMultiplier
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="body2" color="textSecondary">
          <strong>Rider pays:</strong> the full fare shown below.<br />
          <strong>Driver earns (card/mobile money):</strong> {(1 - PLATFORM_FEE) * 100}% of fare — platform keeps {PLATFORM_FEE * 100}%.<br />
          <strong>Driver earns (cash):</strong> collects full fare, deposits {PLATFORM_FEE * 100}% platform fee separately.
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        <Alert severity="info" sx={{ py: 0.5 }}>
          <Typography variant="body2">
            <strong>Fallback defaults:</strong> if the pricing record has never been saved or a field is missing, the system falls back to hardcoded values —
            baseFare: <strong>50 LRD</strong>, distanceRate: <strong>15 LRD/km</strong>, timeRate: <strong>2 LRD/min</strong>, minimumFare: <strong>75 LRD</strong>,
            surgeMultiplier: <strong>×1.0</strong>, tierMultipliers: economy <strong>×1.0</strong> / comfort <strong>×1.4</strong> / business <strong>×1.85</strong>.
            Save this page at least once to ensure live values are always used.
          </Typography>
        </Alert>
      </Paper>

      {TIERS.map(tier => (
        <Box key={tier} mb={3}>
          <Typography variant="subtitle2" gutterBottom>{TIER_LABELS[tier]}</Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>Trip</TableCell>
                <TableCell align="right">Rider pays (LRD)</TableCell>
                <TableCell align="right">Driver earns — card (LRD)</TableCell>
                <TableCell align="right">Driver earns — cash (LRD)</TableCell>
                <TableCell align="right">Platform fee (LRD)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {EXAMPLE_TRIPS.map(trip => {
                const fare = calcFare(trip.distanceKm, trip.durationMin, pricing, tier);
                const driverCard = Math.round(fare * (1 - PLATFORM_FEE));
                const platformFee = fare - driverCard;
                return (
                  <TableRow key={trip.label} hover>
                    <TableCell>
                      {trip.label}
                      <Typography variant="caption" color="textSecondary" display="block">
                        {trip.distanceKm} km · {trip.durationMin} min
                      </Typography>
                    </TableCell>
                    <TableCell align="right"><strong>{fare.toLocaleString()}</strong></TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>{driverCard.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>{fare.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>{platformFee.toLocaleString()}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      ))}

      {pricing.surgeMultiplier > 1.0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Surge is active at ×{pricing.surgeMultiplier.toFixed(2)}. All fares above are already inflated. Riders will see a surge indicator in the app.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Tier matching rules</Typography>
        <Typography variant="body2" color="textSecondary">
          • A <strong>business</strong> driver can accept an <strong>economy</strong> or <strong>comfort</strong> request — they earn the lower tier fare and decide whether it's worth their time.<br />
          • A rider requesting <strong>business</strong> will only be matched with business drivers.<br />
          • Economy multiplier should always be <strong>1.0</strong> (the baseline).
        </Typography>
        <Box display="flex" gap={1} mt={1.5} flexWrap="wrap">
          {TIERS.map(tier => (
            <Chip key={tier} size="small"
              label={`${TIER_LABELS[tier]}: ×${(pricing.tierMultipliers[tier] ?? 1).toFixed(2)}`}
              color={tier === 'business' ? 'primary' : tier === 'comfort' ? 'secondary' : 'default'}
            />
          ))}
        </Box>
      </Paper>
    </Box>
  );
}

function ServiceAreasTab() {
  const [areas, setAreas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drawOpen, setDrawOpen] = useState(false);
  const [viewArea, setViewArea] = useState<any | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const result: any = await client.graphql({ query: LIST_SERVICE_AREAS });
        setAreas(result.data?.listServiceAreas?.items ?? []);
      } catch (e: any) { setError(extractGraphQLError(e, 'Failed to load service areas')); }
      finally { setLoading(false); }
    })();
  }, []);

  const toggle = async (area: any) => {
    const key = `${area.country}/${area.city}`;
    setToggling(key);
    try {
      const result: any = await client.graphql({
        query: UPDATE_SERVICE_AREA,
        variables: { input: { country: area.country, city: area.city, serviceActive: !area.serviceActive } },
      });
      // Check for GraphQL-level errors in the response
      if (result.errors?.length) {
        setError(extractGraphQLError(result, 'Failed to update service area'));
      } else {
        const updated = result.data?.updateServiceArea;
        setAreas(prev => prev.map(a => a.country === area.country && a.city === area.city ? updated : a));
      }
    } catch (e: any) { setError(extractGraphQLError(e, 'Failed to update service area')); }
    finally { setToggling(null); }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Service Areas</Typography>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 600 }}>
          Toggle which areas are actively served. Riders outside active areas will be informed we don't serve their location yet.
          Draw a new polygon to add a city.
        </Typography>
        <Button variant="contained" startIcon={<AddLocation />} onClick={() => setDrawOpen(true)} size="small">
          Add Service Area
        </Button>
      </Box>
      <DrawServiceAreaDialog
        open={drawOpen}
        onClose={() => setDrawOpen(false)}
        onSaved={area => { setAreas(prev => [...prev.filter(a => !(a.country === area.country && a.city === area.city)), area]); }}
      />
      <ViewServiceAreaDialog area={viewArea} onClose={() => setViewArea(null)} />
      {error && <Alert severity={error.includes('permission') ? 'warning' : 'error'} sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {areas.length === 0 ? (
        <Alert severity="info">
          No service areas configured yet. Upload a polygon.json to S3 at <code>geofences/&#123;country&#125;/&#123;city&#125;/polygon.json</code> and it will appear here.
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell>Area</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>City</TableCell>
                <TableCell align="center">Active</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {areas.map(a => (
                <TableRow key={`${a.country}/${a.city}`} hover>
                  <TableCell
                    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline', color: 'primary.main' } }}
                    onClick={() => setViewArea(a)}
                  >
                    {a.displayName}
                  </TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{a.country}</TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{a.city}</TableCell>
                  <TableCell align="center">
                    {toggling === `${a.country}/${a.city}` ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Switch
                        checked={a.serviceActive}
                        onChange={() => toggle(a)}
                        color="success"
                        size="small"
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

function SystemConfigTab() {
  const [otpProvider, setOtpProvider] = useState<'sms' | 'verify'>('sms');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const result: any = await client.graphql({ query: GET_SYSTEM_CONFIG });
        const cfg = result.data?.getSystemConfig;
        if (cfg) setOtpProvider(cfg.otpProvider as 'sms' | 'verify');
      } catch (e: any) { setError(extractGraphQLError(e, 'Failed to load system config')); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true); setError(null); setSaved(false);
    try {
      await client.graphql({ query: UPDATE_SYSTEM_CONFIG, variables: { input: { otpProvider } } });
      setSaved(true);
    } catch (e: any) { setError(extractGraphQLError(e, 'Failed to save system config')); }
    finally { setSaving(false); }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>System Configuration</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>System config saved successfully</Alert>}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Sms color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={600}>OTP Delivery Provider</Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Controls how one-time passwords are sent to users during login. Changes take effect immediately — no redeployment needed.
          </Typography>
          <Grid container spacing={2}>
            {(['sms', 'verify'] as const).map(p => (
              <Grid item xs={12} md={6} key={p}>
                <Paper
                  variant="outlined"
                  onClick={() => setOtpProvider(p)}
                  sx={{
                    p: 2, cursor: 'pointer', transition: 'all 0.15s',
                    borderColor: otpProvider === p ? 'primary.main' : 'divider',
                    bgcolor: otpProvider === p ? 'primary.50' : 'background.paper',
                    '&:hover': { borderColor: 'primary.main' },
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {p === 'sms' ? 'Direct SMS' : 'Twilio Verify'}
                    </Typography>
                    {otpProvider === p && <Chip label="Active" color="primary" size="small" />}
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {p === 'sms'
                      ? 'Sends OTP via Twilio Messages API. Requires 10DLC or toll-free registration. Code is generated and stored server-side.'
                      : 'Twilio manages OTP generation, delivery, and verification. Handles carrier compliance automatically. Recommended for production.'}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
          <Box mt={3}>
            <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

interface PlatformUpdate {
  latestVersion: string;
  minSupportedVersion: string;
  storeUrl: string;
}

type AppKey = 'driver' | 'rider';
type PlatformKey = 'ios' | 'android';

type AppUpdateState = Record<AppKey, Record<PlatformKey, PlatformUpdate>>;

const emptyPlatform: PlatformUpdate = { latestVersion: '', minSupportedVersion: '', storeUrl: '' };
const emptyAppUpdateState = (): AppUpdateState => ({
  driver: { ios: { ...emptyPlatform }, android: { ...emptyPlatform } },
  rider: { ios: { ...emptyPlatform }, android: { ...emptyPlatform } },
});

const versionPattern = /^\d+(\.\d+){0,3}$/;

function AppUpdatesTab() {
  const [state, setState] = useState<AppUpdateState>(emptyAppUpdateState);
  const [updateMessage, setUpdateMessage] = useState('');
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const result: any = await client.graphql({ query: GET_APP_UPDATE_CONFIG });
        const cfg = result.data?.getAppUpdateConfig;
        if (cfg) {
          const next = emptyAppUpdateState();
          (['driver', 'rider'] as AppKey[]).forEach(appKey => {
            (['ios', 'android'] as PlatformKey[]).forEach(pf => {
              const p = cfg[appKey]?.[pf];
              if (p) next[appKey][pf] = {
                latestVersion: p.latestVersion ?? '',
                minSupportedVersion: p.minSupportedVersion ?? '',
                storeUrl: p.storeUrl ?? '',
              };
            });
          });
          setState(next);
          setUpdateMessage(cfg.updateMessage ?? '');
          setUpdatedAt(cfg.updatedAt ?? null);
        }
      } catch (e: any) {
        setError(extractGraphQLError(e, 'Failed to load app update config'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const validPlatform = (p: PlatformUpdate) => {
    if (p.latestVersion && !versionPattern.test(p.latestVersion)) return false;
    if (p.minSupportedVersion && !versionPattern.test(p.minSupportedVersion)) return false;
    if (p.storeUrl && !p.storeUrl.startsWith('https://')) return false;
    return true;
  };
  const canSave = (['driver', 'rider'] as AppKey[]).every(a =>
    (['ios', 'android'] as PlatformKey[]).every(pf => validPlatform(state[a][pf]))
  );

  const setPlatform = (appKey: AppKey, pf: PlatformKey, value: PlatformUpdate) =>
    setState(s => ({ ...s, [appKey]: { ...s[appKey], [pf]: value } }));

  const handleSave = async () => {
    setSaving(true); setError(null); setSaved(false);
    try {
      const appInput = (a: AppKey) => ({
        ios: { ...state[a].ios },
        android: { ...state[a].android },
      });
      const input = { driver: appInput('driver'), rider: appInput('rider'), updateMessage };
      const result: any = await client.graphql({ query: UPDATE_APP_UPDATE_CONFIG, variables: { input } });
      if (result.errors?.length) {
        setError(extractGraphQLError(result, 'Failed to save app update config'));
      } else {
        setUpdatedAt(result.data?.updateAppUpdateConfig?.updatedAt ?? new Date().toISOString());
        setSaved(true);
      }
    } catch (e: any) {
      setError(extractGraphQLError(e, 'Failed to save app update config'));
    } finally {
      setSaving(false);
    }
  };

  const platformCard = (appKey: AppKey, pf: PlatformKey) => {
    const value = state[appKey][pf];
    const isIos = pf === 'ios';
    const title = `${appKey === 'driver' ? 'Driver' : 'Rider'} — ${isIos ? 'iOS (App Store)' : 'Android (Play Store)'}`;
    const storeHelper = isIos
      ? 'App Store listing link (apps.apple.com).'
      : 'Play Store listing link (play.google.com).';
    return (
      <Grid item xs={12} md={6} key={`${appKey}-${pf}`}>
        <Card variant="outlined" sx={{ height: '100%' }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              {isIos ? <Apple color="action" /> : <Android color="action" />}
              <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Latest Version" value={value.latestVersion}
                  onChange={e => setPlatform(appKey, pf, { ...value, latestVersion: e.target.value.trim() })}
                  error={!!value.latestVersion && !versionPattern.test(value.latestVersion)}
                  placeholder="1.2.0"
                  helperText="Newest version in the store. Users below this get a suggested update." />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Minimum Supported Version" value={value.minSupportedVersion}
                  onChange={e => setPlatform(appKey, pf, { ...value, minSupportedVersion: e.target.value.trim() })}
                  error={!!value.minSupportedVersion && !versionPattern.test(value.minSupportedVersion)}
                  placeholder="1.0.0"
                  helperText="Users below this are forced to update before continuing." />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Store URL" value={value.storeUrl}
                  onChange={e => setPlatform(appKey, pf, { ...value, storeUrl: e.target.value.trim() })}
                  error={!!value.storeUrl && !value.storeUrl.startsWith('https://')}
                  placeholder="https://…"
                  helperText={storeHelper} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const appSection = (appKey: AppKey) => (
    <Box mb={2}>
      <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1, mb: 1 }}>
        {appKey === 'driver' ? 'Driver App' : 'Rider App'}
      </Typography>
      <Grid container spacing={3}>
        {platformCard(appKey, 'ios')}
        {platformCard(appKey, 'android')}
      </Grid>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom>App Update Management</Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Control forced and suggested updates for all four apps (Driver &amp; Rider, each on iOS and Android).
        When a user's installed version is below the minimum supported version, they are blocked until they update.
        When it is below the latest version, they see a dismissible prompt. Changes take effect the next time an app
        launches — no redeployment needed.
      </Typography>
      {error && <Alert severity={error.includes('permission') ? 'warning' : 'error'} sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>App update config saved successfully</Alert>}
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <>
          {appSection('driver')}
          <Divider sx={{ my: 2 }} />
          {appSection('rider')}
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Update Message (optional)" value={updateMessage}
                onChange={e => setUpdateMessage(e.target.value)} multiline minRows={2}
                placeholder="A new version is available with improvements and bug fixes."
                helperText="Shown on the update prompt/screen for all apps. Leave blank to use the default message." />
            </Grid>
          </Grid>
          <Box mt={3} display="flex" alignItems="center" gap={2}>
            <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving || !canSave}>
              {saving ? 'Saving…' : 'Save Update Settings'}
            </Button>
            {updatedAt && (
              <Typography variant="caption" color="textSecondary">
                Last updated: {new Date(updatedAt).toLocaleString()}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}

interface ReviewAccountRow {
  phoneNumber: string;
  code: string;
  label: string;
}

const phonePattern = /^\+[1-9]\d{6,14}$/;   // E.164
const codePattern = /^\d{6}$/;

function ReviewAccountsTab() {
  const [rows, setRows] = useState<ReviewAccountRow[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const result: any = await client.graphql({ query: GET_REVIEW_ACCOUNTS });
        const cfg = result.data?.getReviewAccounts;
        setRows((cfg?.accounts ?? []).map((a: any) => ({
          phoneNumber: a.phoneNumber ?? '',
          code: a.code ?? '',
          label: a.label ?? '',
        })));
        setUpdatedAt(cfg?.updatedAt ?? null);
      } catch (e: any) {
        setError(extractGraphQLError(e, 'Failed to load reviewer accounts'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setRow = (idx: number, value: Partial<ReviewAccountRow>) =>
    setRows(prev => prev.map((r, i) => (i === idx ? { ...r, ...value } : r)));
  const addRow = () => setRows(prev => [...prev, { phoneNumber: '', code: '', label: '' }]);
  const removeRow = (idx: number) => setRows(prev => prev.filter((_, i) => i !== idx));

  const rowValid = (r: ReviewAccountRow) =>
    phonePattern.test(r.phoneNumber.replace(/\s/g, '')) && codePattern.test(r.code.trim());
  const dupePhones = (() => {
    const digits = rows.map(r => r.phoneNumber.replace(/[^\d]/g, ''));
    return new Set(digits.filter((d, i) => d && digits.indexOf(d) !== i));
  })();
  const canSave = rows.every(rowValid) && dupePhones.size === 0;

  const handleSave = async () => {
    setSaving(true); setError(null); setSaved(false);
    try {
      const input = {
        accounts: rows.map(r => ({
          phoneNumber: r.phoneNumber.replace(/\s/g, ''),
          code: r.code.trim(),
          ...(r.label.trim() ? { label: r.label.trim() } : {}),
        })),
      };
      const result: any = await client.graphql({ query: UPDATE_REVIEW_ACCOUNTS, variables: { input } });
      if (result.errors?.length) {
        setError(extractGraphQLError(result, 'Failed to save reviewer accounts'));
      } else {
        setUpdatedAt(result.data?.updateReviewAccounts?.updatedAt ?? new Date().toISOString());
        setSaved(true);
      }
    } catch (e: any) {
      setError(extractGraphQLError(e, 'Failed to save reviewer accounts'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <VerifiedUser color="primary" fontSize="small" />
        <Typography variant="h6">Store Reviewer Accounts</Typography>
      </Box>
      <Typography variant="body2" color="textSecondary" paragraph>
        Fixed one-time-password sign-in accounts for App Store / Play Store reviewers. These phone numbers
        bypass Twilio SMS and accept the fixed code shown here, in <strong>all environments including production</strong>,
        so reviewers can sign in without receiving a real SMS. Changes take effect immediately — no redeployment needed.
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        These are permanent fixed-code logins in production. Keep the numbers controlled, use codes that are not guessable
        for real users, and remove an entry once a review cycle no longer needs it. The reviewer account must still exist
        in the Cognito pool with a real profile (rider profile, or driver with approved KYC).
      </Alert>
      {error && <Alert severity={error.includes('permission') ? 'warning' : 'error'} sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>Reviewer accounts saved successfully</Alert>}
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ width: '28%' }}>Phone Number (E.164)</TableCell>
                  <TableCell sx={{ width: '18%' }}>Fixed Code</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell align="center" sx={{ width: 56 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="textSecondary" sx={{ py: 1 }}>
                        No reviewer accounts configured. Add one below.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : rows.map((r, idx) => {
                  const phoneBad = !!r.phoneNumber && !phonePattern.test(r.phoneNumber.replace(/\s/g, ''));
                  const isDupe = dupePhones.has(r.phoneNumber.replace(/[^\d]/g, ''));
                  const codeBad = !!r.code && !codePattern.test(r.code.trim());
                  return (
                    <TableRow key={idx}>
                      <TableCell>
                        <TextField fullWidth size="small" value={r.phoneNumber}
                          onChange={e => setRow(idx, { phoneNumber: e.target.value })}
                          error={phoneBad || isDupe}
                          placeholder="+13308601111"
                          helperText={isDupe ? 'Duplicate number' : phoneBad ? 'Must be E.164 (+ then digits)' : ' '} />
                      </TableCell>
                      <TableCell>
                        <TextField fullWidth size="small" value={r.code}
                          onChange={e => setRow(idx, { code: e.target.value.replace(/[^\d]/g, '').slice(0, 6) })}
                          error={codeBad}
                          placeholder="111111"
                          helperText={codeBad ? '6 digits' : ' '} />
                      </TableCell>
                      <TableCell>
                        <TextField fullWidth size="small" value={r.label}
                          onChange={e => setRow(idx, { label: e.target.value })}
                          placeholder="LibRide Driver — App Store review"
                          helperText=" " />
                      </TableCell>
                      <TableCell align="center">
                        <Button color="error" size="small" onClick={() => removeRow(idx)} sx={{ minWidth: 0 }}>
                          <DeleteOutline fontSize="small" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box mt={2} display="flex" alignItems="center" gap={2}>
            <Button startIcon={<AddIcon />} onClick={addRow} size="small">Add Account</Button>
          </Box>
          <Box mt={3} display="flex" alignItems="center" gap={2}>
            <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving || !canSave}>
              {saving ? 'Saving…' : 'Save Reviewer Accounts'}
            </Button>
            {updatedAt && (
              <Typography variant="caption" color="textSecondary">
                Last updated: {new Date(updatedAt).toLocaleString()}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}

function Settings() {
  const [tabValue, setTabValue] = useState(0);
  const [pricing, setPricing] = useState({
    baseFare: 50, distanceRate: 15, timeRate: 2, minimumFare: 75, surgeMultiplier: 1.0,
    maxTripDurationMinutes: 180,
    tierMultipliers: { economy: 1.0, comfort: 1.4, business: 1.85 },
    serviceAreaEnforced: true,
  });
  const [loadingPricing, setLoadingPricing] = useState(true);
  const [savingPricing, setSavingPricing] = useState(false);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const result: any = await client.graphql({ query: GET_PRICING_CONFIG });
        const cfg = result.data?.getPricingConfig;
        if (cfg) setPricing({
          baseFare: cfg.baseFare, distanceRate: cfg.distanceRate,
          timeRate: cfg.timeRate, minimumFare: cfg.minimumFare,
          surgeMultiplier: cfg.surgeMultiplier ?? 1.0,
          maxTripDurationMinutes: cfg.maxTripDurationMinutes ?? 180,
          tierMultipliers: {
            economy:  cfg.tierMultipliers?.economy  ?? 1.0,
            comfort:  cfg.tierMultipliers?.comfort  ?? 1.4,
            business: cfg.tierMultipliers?.business ?? 1.85,
          },
          serviceAreaEnforced: cfg.serviceAreaEnforced ?? true,
        });
      } catch (e: any) {
        setPricingError(extractGraphQLError(e, 'Failed to load pricing config'));
      } finally {
        setLoadingPricing(false);
      }
    })();
  }, []);

  const handleSavePricing = async () => {
    setSavingPricing(true); setPricingError(null);
    try {
      const result: any = await client.graphql({ query: UPDATE_PRICING_CONFIG, variables: { input: pricing } });
      // Check for GraphQL-level errors in the response
      if (result.errors?.length) {
        setPricingError(extractGraphQLError(result, 'Failed to save pricing config'));
      } else {
        setSnackbar('Pricing configuration saved successfully');
      }
    } catch (e: any) {
      setPricingError(extractGraphQLError(e, 'Failed to save pricing config'));
    } finally {
      setSavingPricing(false);
    }
  };

  const field = (label: string, key: keyof typeof pricing, helper: string, prefix = 'LRD') => (
    <Grid item xs={12} md={6}>
      <TextField fullWidth label={label} type="number" value={pricing[key]}
        onChange={e => setPricing(p => ({ ...p, [key]: parseFloat(e.target.value) || 0 }))}
        InputProps={{ startAdornment: <InputAdornment position="start">{prefix}</InputAdornment> }}
        helperText={helper}
        inputProps={{ step: key === 'surgeMultiplier' ? 0.1 : 1, min: key === 'surgeMultiplier' ? 1 : 0 }}
      />
    </Grid>
  );

  const tierField = (label: string, tier: 'economy' | 'comfort' | 'business', helper: string) => (
    <Grid item xs={12} md={4}>
      <TextField fullWidth label={label} type="number" value={pricing.tierMultipliers[tier]}
        onChange={e => setPricing(p => ({ ...p, tierMultipliers: { ...p.tierMultipliers, [tier]: parseFloat(e.target.value) || 1 } }))}
        InputProps={{ startAdornment: <InputAdornment position="start">×</InputAdornment> }}
        helperText={helper} inputProps={{ step: 0.05, min: 1 }}
      />
    </Grid>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>System Settings</Typography>
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label="Pricing" icon={<AttachMoney />} iconPosition="start" />
            <Tab label="Service Areas" icon={<MapIcon />} iconPosition="start" />
            <Tab label="System" icon={<SettingsIcon />} iconPosition="start" />
            <Tab label="App Updates" icon={<SystemUpdate />} iconPosition="start" />
            <Tab label="Reviewer Accounts" icon={<VerifiedUser />} iconPosition="start" />
            <Tab label="Security" icon={<Security />} iconPosition="start" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {/* Pricing tab content */}
          <Typography variant="h6" gutterBottom>Pricing Configuration</Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            All amounts in Liberian Dollars (LRD). Changes take effect immediately for new ride requests. In-progress trips are not affected.
          </Typography>
          {pricingError && (
            <Alert severity={pricingError.includes('permission') ? 'warning' : 'error'} sx={{ mb: 2 }}>
              {pricingError}
            </Alert>
          )}
          {loadingPricing ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : (
            <>
              <Grid container spacing={3}>
                {field('Base Fare', 'baseFare', 'Flat fee charged at the start of every trip')}
                {field('Distance Rate (per km)', 'distanceRate', 'Added per kilometer of the route')}
                {field('Time Rate (per minute)', 'timeRate', 'Added per minute — accounts for traffic')}
                {field('Minimum Fare', 'minimumFare', 'Floor price — no trip can be cheaper than this')}
                {field('Surge Multiplier', 'surgeMultiplier', 'Set > 1.0 during peak demand', '×')}
                {field('Max Trip Duration (minutes)', 'maxTripDurationMinutes', 'Trips longer than this are declined — default 180 (3 hrs)', 'min')}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={pricing.serviceAreaEnforced}
                        onChange={e => setPricing(p => ({ ...p, serviceAreaEnforced: e.target.checked }))}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>Service Area Enforcement</Typography>
                        <Typography variant="caption" color="textSecondary">
                          When off, riders anywhere can request rides (useful for testing). When on, only riders within active service area polygons can request.
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>Vehicle Tier Multipliers</Typography>
                  <Typography variant="caption" color="textSecondary">Applied after all other calculations. Economy = 1.0 is the baseline.</Typography>
                </Grid>
                {tierField('Economy ×', 'economy', 'Baseline — keep at 1.0')}
                {tierField('Comfort ×', 'comfort', 'Recommended: 1.3 – 1.5')}
                {tierField('Business ×', 'business', 'Recommended: 1.7 – 2.0')}
              </Grid>
              <Box mt={3}>
                <Button variant="contained" startIcon={<Save />} onClick={handleSavePricing} disabled={savingPricing}>
                  {savingPricing ? 'Saving…' : 'Save Pricing Settings'}
                </Button>
              </Box>
              <PricingImpactPanel pricing={pricing} />
            </>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <ServiceAreasTab />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <SystemConfigTab />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <AppUpdatesTab />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <ReviewAccountsTab />
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <Typography variant="h6" gutterBottom>Security & Privacy</Typography>
          <Alert severity="info">Security settings are managed through AWS Cognito and IAM. Contact your system administrator for changes.</Alert>
        </TabPanel>

      </Card>
      <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar('')} message={snackbar} />
    </Box>
  );
}

export default Settings;
